import { Link } from 'react-router-dom';
import { Shield, Mail, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TranslatedText } from '@/components/TranslatedText';

const SpotifyIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
  </svg>
);

const socialLinks = [
  {
    name: 'Spotify',
    icon: SpotifyIcon,
    url: 'https://open.spotify.com/show/1VnoF1p9A8NpZlHGFv60B3?si=NwChlsmVQR2MOP-JsaMNNA',
    description: 'Podcast',
    color: 'hover:text-green-400'
  }
];

export const LightSalesFooter = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-900 text-white pb-24 sm:pb-10">
      {/* Final CTA */}
      <div className="py-12 sm:py-16 px-4 text-center border-b border-slate-800">
        <h2 className="text-2xl sm:text-3xl font-bold mb-4">
          <TranslatedText>Start With 1 Free Class</TranslatedText>
        </h2>
        <p className="text-slate-400 text-base mb-6">
          <TranslatedText>No credit card required.</TranslatedText>
        </p>
        <Link to="/apply?plan=free">
          <Button
            size="lg"
            className="bg-primary hover:bg-primary/90 text-primary-foreground h-14 px-10 text-base font-semibold rounded-xl active:scale-[0.98] shadow-lg"
          >
            <Play className="w-5 h-5 mr-2" />
            <TranslatedText>Watch 1 Free Class</TranslatedText>
          </Button>
        </Link>
      </div>

      <div className="py-10 px-4">
        <div className="max-w-lg mx-auto">
          {/* Logo */}
          <div className="text-center mb-6">
            <h2 className="text-xl font-bold mb-2">
              Faceless Aura <span className="text-blue-400">Club</span>
            </h2>
            <p className="text-slate-400 text-sm">
              <TranslatedText>Structured AI workflows for faceless digital assets.</TranslatedText>
            </p>
          </div>

          {/* Social */}
          <div className="flex justify-center gap-4 mb-6">
            {socialLinks.map((social) => (
              <a
                key={social.name}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`p-3 rounded-full bg-slate-800 text-slate-400 transition-all duration-300 ${social.color} hover:bg-slate-700 hover:scale-110`}
                aria-label={social.name}
                title={social.description}
              >
                <social.icon />
              </a>
            ))}
          </div>

          {/* Trust */}
          <div className="flex flex-wrap justify-center gap-4 mb-6 text-sm text-slate-400">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-green-400" />
              <span><TranslatedText>7-Day Guarantee</TranslatedText></span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-blue-400" />
              <a href="mailto:hello@auradynamics.management" className="hover:text-white transition-colors">
                <TranslatedText>Contact Support</TranslatedText>
              </a>
            </div>
          </div>

          {/* Links */}
          <div className="flex flex-wrap justify-center gap-4 mb-6 text-sm">
            <Link to="/login" className="text-slate-400 hover:text-white transition-colors py-2">
              <TranslatedText>Login</TranslatedText>
            </Link>
            <Link to="/terms" className="text-slate-400 hover:text-white transition-colors py-2">
              <TranslatedText>Terms</TranslatedText>
            </Link>
            <Link to="/privacy" className="text-slate-400 hover:text-white transition-colors py-2">
              <TranslatedText>Privacy</TranslatedText>
            </Link>
            <Link to="/refunds" className="text-slate-400 hover:text-white transition-colors py-2">
              <TranslatedText>Refunds</TranslatedText>
            </Link>
            <Link to="/cookies" className="text-slate-400 hover:text-white transition-colors py-2">
              <TranslatedText>Cookies</TranslatedText>
            </Link>
            <Link to="/legal" className="text-slate-400 hover:text-white transition-colors py-2">
              <TranslatedText>Legal Notice</TranslatedText>
            </Link>
            <Link to="/company" className="text-slate-400 hover:text-white transition-colors py-2">
              <TranslatedText>Company</TranslatedText>
            </Link>
            <Link to="/payments-policy" className="text-slate-400 hover:text-white transition-colors py-2">
              <TranslatedText>Payments Policy</TranslatedText>
            </Link>
          </div>

          {/* Last updated note */}
          <div className="text-center text-xs text-slate-500 mb-4">
            <TranslatedText>Terms, Privacy & Cookie Policy last updated:</TranslatedText> May 13, 2026
          </div>

          {/* Disclaimer */}
          <div className="text-center text-xs text-slate-500 mb-6">
            <p>
              <TranslatedText>Results depend on execution, niche selection, and market conditions. No income guarantees are made.</TranslatedText>
            </p>
          </div>

          {/* Company link */}
          <div className="text-center text-xs text-slate-400 pt-6 border-t border-slate-800 mb-4">
            <p>
              <TranslatedText>Faceless Aura Club is a brand operated by</TranslatedText>{' '}
              <Link to="/company" className="text-slate-300 hover:text-white font-medium underline underline-offset-2">
                AURADYNAMICS PROJECT MANAGEMENT SERVICES - FZCO
              </Link>
            </p>
          </div>

          {/* Copyright */}
          <div className="text-center text-xs text-slate-500 pt-6 border-t border-slate-800">
            <p>© {currentYear} AURADYNAMICS PROJECT MANAGEMENT SERVICES - FZCO. <TranslatedText>All Rights Reserved.</TranslatedText></p>
            <p className="mt-1">Faceless Aura Club™ <TranslatedText>is a trademark of AURADYNAMICS PROJECT MANAGEMENT SERVICES - FZCO.</TranslatedText></p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default LightSalesFooter;
